import { useState } from 'react';
import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import Events from '@activebrands/core-web/libs/Events';
import { CheckoutEvents } from '@activebrands/core-web/libs/Events/types';
import { CheckoutTracking } from '@activebrands/core-web/libs/Tracking/constants';
import { styled } from '@activebrands/core-web/libs/styletron';
import IngridWidget from '@grebban/centra-ingrid-delivery-react';
import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';
import StateButton from 'components/buttons/StateButton';
import DefaultShipping from './DefaultShipping';

const Div = styled('div');

const Wrapper = styled('div', {
    position: 'relative',
    display: 'block',
    minHeight: '100%',
    padding: '0 var(--margin-content)',
});

const ShippingMethods = ({ $style = {}, currentCheckoutStep, deliveryStep, setCurrentCheckoutStep }) => {
    const currentBasket = useSelector(state => state.basket);
    const { currency, shippingMethodId, shippingMethods, cloudflareTurnstileStatus, isFetching } = currentBasket;
    const cloudflareTurnstileNeedsAction = cloudflareTurnstileStatus === 'need action';
    const cloudflareTurnstilePending = cloudflareTurnstileStatus === 'pending';
    const cloudflareTurnstileSuccess = cloudflareTurnstileStatus === 'success';

    const [isLoading, setIsLoading] = useState(false);

    const [shippingState, updateShippingState] = useState({
        error: '',
        forceFallbackShipping: false,
        valid: false,
    });

    const onError = () => {
        updateShippingState({
            ...shippingState,
            forceFallbackShipping: true,
        });
    };

    const onLoad = () => {
        Events.trigger(CheckoutEvents.OPTION, {
            step: CheckoutTracking.SHIPPING_STEP,
            option: shippingMethodId,
            currencyCode: currency,
        });
    };

    const eventCallback = (eventName, data) => {
        setIsLoading(true);
        if (eventName === 'data_changed') {
            const shippingData = data?.data ? objectKeysToCamelCase(data.data) : undefined;

            // if Ingrid isn't loaded/available we track centras default shipping.
            Events.trigger(CheckoutEvents.SHIPPING, { shippingTier: shippingData?.shippingMethod ?? 'default' });
            updateShippingState({ ...shippingState, valid: true });

            try {
                Events.trigger(CheckoutEvents.SHIPPING_INFO_ADDED, {
                    shippingInfo: {
                        currency: currency,
                        shipping_tier: data?.data?.categoryName,
                    },
                    items: currentBasket.items,
                });
            } catch (e) {
                console.warn('Unable to trigger shipping event.');
            }
        } else if (eventName === 'no_shipping_options') {
            updateShippingState({ ...shippingState, valid: false });
            setIsLoading(false);
        }
        setIsLoading(false);
    };

    // Check if Ingrid is available. If it is, we ALWAYS want to use it (unless Ingrid gave an error and we force the fallback)
    let ShippingMethod = DefaultShipping;
    let usedShippingMethod = shippingMethodId;

    if (
        !shippingState.forceFallbackShipping &&
        shippingMethods?.ingrid !== undefined &&
        shippingMethods?.ingrid.snippet !== null
    ) {
        console.log('ingrid is available')
        usedShippingMethod = 'ingrid';
        ShippingMethod = IngridWidget;
    }

    if (currentCheckoutStep === deliveryStep) {
        return (
            <Wrapper $style={$style}>
                <ShippingMethod
                    data={shippingMethods[usedShippingMethod]}
                    eventCallback={eventCallback}
                    updateShippingState={updateShippingState}
                    onError={onError}
                    onLoad={onLoad}
                />
                <Div
                    $style={{
                        display: cloudflareTurnstileNeedsAction ? 'flex' : 'none',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '24px',
                    }}
                    id="cloudflare-turnstile-widget"
                />
                <StateButton
                    $style={{ width: ['100%', null, 'auto'], marginTop: '24px' }}
                    disabled={!shippingState.valid || isFetching || !cloudflareTurnstileSuccess}
                    state={isLoading || isFetching || cloudflareTurnstilePending ? 'loading' : 'arrow'}
                    theme="defaultsecondary"
                    onClick={() => {
                        setCurrentCheckoutStep(deliveryStep + 1);
                    }}
                >
                    {fm('Continue to payment')}
                </StateButton>
            </Wrapper>
        );
    }

    return null;
};

ShippingMethods.propTypes = {
    $style: PropTypes.object,
    currentCheckoutStep: PropTypes.number,
    deliveryStep: PropTypes.number,
    setCurrentCheckoutStep: PropTypes.func,
};

export default ShippingMethods;
